import { useCallback, useMemo, useState, useRef } from 'react'
import Draggable from 'react-draggable'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Tooltip } from 'antd'
import * as AntIcons from '@ant-design/icons'
import PropTypes from 'prop-types'

import { sizes } from '@qflow/theme'
import {
  Container,
  InnerContainer,
  ActionStripCard,
  AssignmentButtonGroup
} from 'portal/components/CommonStyles/ActionStripStyledComponents'
import { ALERT_STATUS_MAPPER } from 'portal/components/RecordsModules/common/alerts'

import { useModuleIds, useUserProjectRole } from 'portal/lib/hooks'
import {
  icons,
  Clickable,
  Text,
  Padding,
  ColourButton,
  Spacer,
  Spinner
} from 'portal/lib/primitives'
import { handleApiFailure } from 'portal/lib/sharedComponents/apiFailureHandler'
import { AssignmentLongRunningOperationModal } from 'portal/lib/sharedComponents/AssignmentLongRunningOperationModal'

import {
  assignRecords,
  unassignRecords,
  dismissEvents
} from 'portal/store/events/actions'
import { projectAlertClientDismissBulk } from 'portal/store/projectAlerts/actions'
import { recordAlertBulkStatusChanged } from 'portal/store/records/alerts/actions'

import { AssignmentStrip } from './AssignmentStrip'
import { PROJECT_LEVEL_ALERTS_PREFIX } from './constants'
import { UnassignmentStrip } from './UnassignmentStrip'
import { UpdateSection } from './UpdateSection'

export function ActionStrip({
  eventKeys,
  eventRecordMapping,
  selectedEventsContainUnloaded,
  onClose
}) {
  const LONG_RUNNING_WARNING_THRESHOLD = 1000
  const MAX_ASSIGNMENT_LIMIT = 10000

  const MAX_DISMISSALS = 100
  const MAX_UPDATE = 100

  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()
  const [showAssignmentStrip, setShowAssignmentStrip] = useState(false)
  const [showUnassignmentStrip, setShowUnassignmentStrip] = useState(false)
  const [showUpdateSection, setShowUpdateSection] = useState(false)
  const [projectLevelAlertIds, setProjectLevelAlertIds] = useState([])

  const [showDismissConfirmationModal, setShowDismissConfirmationModal] =
    useState(false)
  const [showLongRunningOperationModal, setShowLongRunningOperationModal] =
    useState(false)
  const { isProjectAdmin } = useUserProjectRole(accountId, projectId)
  const draggableRef = useRef(null)

  const projectUsers = useSelector(state => state.projectUsers)
  const assign = useSelector(state => state.events.assign)
  const unassign = useSelector(state => state.events.unassign)
  const dismissal = useSelector(state => state.events.dismissal)

  const isWasteModule = moduleName === 'waste'

  const validSelectedEventKeys = useMemo(() => {
    // First filter out Project Level Alerts, these have been prefixed with PROJECT_LEVEL_ALERTS_PREFIX in the table rowKey (see AlertsTable.tsx).
    // We will remove this prefix (using javascript's string slice) and store the IDs for use when
    // dismissing Project Level Alerts or assigning/unassigning users to the related Records.
    const plaKeys = eventKeys
      .filter(eventKey => eventKey.startsWith(PROJECT_LEVEL_ALERTS_PREFIX))
      .map(eventKey => eventKey.slice(PROJECT_LEVEL_ALERTS_PREFIX.length))
    setProjectLevelAlertIds(plaKeys)

    // EventLog has polling to update the event list periodically, so other users or slow server dismiss
    // requests can cause events to be removed from the list while the user still has them selected.
    // This is mostly a problem when the EventLog is filtered, as the Events go out of view.
    eventKeys
      .filter(eventKey => !eventKey.startsWith(PROJECT_LEVEL_ALERTS_PREFIX))
      .filter(eventKey => !eventRecordMapping[eventKey])
      .forEach(eventKey => {
        console.warn(`Event key ${eventKey} selected, but has gone away.`)
      })
    return eventKeys.filter(eventKey => eventRecordMapping[eventKey])
  }, [eventKeys, eventRecordMapping])

  // Use selected Events to create a set of unique Records
  const relatedRecords = useMemo(() => {
    const relatedRecordIds = new Set()
    validSelectedEventKeys.forEach(eventKey => {
      if (eventRecordMapping[eventKey].recordId) {
        relatedRecordIds.add(eventRecordMapping[eventKey].recordId)
      }
    })
    return relatedRecordIds
  }, [validSelectedEventKeys, eventRecordMapping])

  // Use selected Events to create a set of unique users who are assigned to 1 or more events/records
  const relatedAssignees = useMemo(() => {
    const assignedUsers = {}
    validSelectedEventKeys.forEach(eventKey => {
      if (eventRecordMapping[eventKey].assignedTo) {
        eventRecordMapping[eventKey].assignedTo.forEach(assignee => {
          assignedUsers[assignee.userId] = {
            userId: assignee.userId,
            displayName: assignee.userName,
            email: projectUsers.users[`${projectId}_${assignee.userId}`]?.email
          }
        })
      }
    })
    if (Object.keys(assignedUsers).length === 0) {
      setShowUnassignmentStrip(false)
    }
    return assignedUsers
  }, [
    validSelectedEventKeys,
    eventRecordMapping,
    projectId,
    projectUsers.users
  ])

  // Disable Update button if selected events does not contain description 'No waste carrier licence'
  const selectedEventsContainNoWasteCarrierLicence = useMemo(() => {
    return validSelectedEventKeys.some(eventKey => {
      const event = eventRecordMapping[eventKey]
      return (
        event.description &&
        event.description.includes('No waste carrier licence')
      )
    })
  }, [validSelectedEventKeys, eventRecordMapping])

  const handleAssignBtnClick = () => {
    setShowUnassignmentStrip(false)
    setShowUpdateSection(false)
    setShowAssignmentStrip(!showAssignmentStrip)
  }

  const handleUnassignBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUpdateSection(false)
    setShowUnassignmentStrip(!showUnassignmentStrip)
  }

  const handleUpdateBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUnassignmentStrip(false)

    setShowUpdateSection(!showUpdateSection)
  }

  const handleDismissBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUnassignmentStrip(false)
    setShowDismissConfirmationModal(!showDismissConfirmationModal)
  }

  const handleRecordsAssign = useCallback(
    assignTo => {
      dispatch(
        assignRecords(accountId, projectId, relatedRecords, assignTo, () => {
          setShowLongRunningOperationModal(false)
          setTimeout(() => {
            onClose?.(true)
          }, 500)
        })
      )
      setShowAssignmentStrip(false)
      if (
        relatedRecords.size * assignTo.length >
        LONG_RUNNING_WARNING_THRESHOLD
      ) {
        setShowLongRunningOperationModal(true)
      }
    },
    [accountId, dispatch, relatedRecords, onClose, projectId]
  )

  const handleRecordsUnassign = useCallback(
    unassignUsers => {
      dispatch(
        unassignRecords(
          accountId,
          projectId,
          relatedRecords,
          unassignUsers,
          () => {
            setShowLongRunningOperationModal(false)
            setTimeout(() => {
              onClose?.(true)
            }, 500)
          }
        )
      )
      setShowUnassignmentStrip(false)
      if (
        relatedRecords.size * unassignUsers.length >
        LONG_RUNNING_WARNING_THRESHOLD
      ) {
        setShowLongRunningOperationModal(true)
      }
    },
    [accountId, dispatch, relatedRecords, onClose, projectId]
  )

  const handleDismissModalCancel = () => {
    setShowDismissConfirmationModal(false)
  }

  const handleEventsDismissal = useCallback(() => {
    setShowDismissConfirmationModal(false)

    // We expect to have either infractions Alerts, or Record level and/or Project level alerts
    // in the selection, but just in case...

    // First bulk dismiss the Project Level Advanced Alerts
    if (projectLevelAlertIds.length > 0) {
      dispatch(
        projectAlertClientDismissBulk(
          accountId,
          projectId,
          projectLevelAlertIds,
          () => {
            onClose?.(true)
          },
          handleApiFailure
        )
      )
    }

    // Then handle the other alert types
    const eventsToDismiss = validSelectedEventKeys.slice(0, MAX_DISMISSALS) // Default order is that which they clicked them in

    // Split out Record Level Advanced Alerts from the Infraction Events
    const advancedAlertsToDismiss = eventsToDismiss.filter(
      eventKey => eventRecordMapping[eventKey].isRecordLevelAlert
    )
    const infractionEventsToDismiss = eventsToDismiss.filter(
      eventKey => !eventRecordMapping[eventKey].isRecordLevelAlert
    )

    // Dismiss the Record Level Advanced Alerts
    if (advancedAlertsToDismiss.length > 0) {
      dispatch(
        recordAlertBulkStatusChanged(
          accountId,
          projectId,
          advancedAlertsToDismiss,
          ALERT_STATUS_MAPPER.clientDismissed,
          () => {
            onClose?.(true)
          },
          handleApiFailure
        )
      )
    }

    // Dismiss Infraction Alerts (events)
    if (infractionEventsToDismiss.length > 0) {
      dispatch(
        dismissEvents(
          accountId,
          projectId,
          infractionEventsToDismiss,
          () => {
            onClose?.(true)
          },
          handleApiFailure
        )
      )
    }
  }, [
    projectLevelAlertIds,
    validSelectedEventKeys,
    dispatch,
    accountId,
    projectId,
    eventRecordMapping,
    onClose
  ])

  const working = dismissal.loading || assign.loading || unassign.loading
  const visible =
    validSelectedEventKeys?.length > 0 || projectLevelAlertIds.length > 0
  const showUnassignBtn =
    Object.keys(relatedAssignees).length > 0 || selectedEventsContainUnloaded

  return (
    <Container $visible={visible}>
      {working && <Spinner />}
      <Draggable bounds="body" nodeRef={draggableRef}>
        <InnerContainer ref={draggableRef} style={{ cursor: 'move' }}>
          <ActionStripCard>
            <Padding noVertical>
              <Text bold size={sizes.MEDIUM}>
                {validSelectedEventKeys.length + projectLevelAlertIds.length}{' '}
                selected
              </Text>
            </Padding>

            {isProjectAdmin && (
              <AssignmentButtonGroup>
                <ColourButton.Basic
                  type="button"
                  onClick={handleAssignBtnClick}
                  disabled={working || validSelectedEventKeys.length < 1}
                  data-testid="Assign"
                  className={showAssignmentStrip ? 'selected' : ''}
                  style={{
                    borderRadius:
                      showUnassignBtn || isWasteModule ? '5px 0 0 5px' : '5px'
                  }}
                >
                  <icons.AddPerson colour="currentColor" size={sizes.MEDIUM} />
                  <Spacer.Fine />
                  Assign
                </ColourButton.Basic>

                {showUnassignBtn && (
                  <ColourButton.Basic
                    type="button"
                    data-testid="Unassign"
                    onClick={handleUnassignBtnClick}
                    disabled={working}
                    className={showUnassignmentStrip ? 'selected' : ''}
                    style={{
                      borderLeft: 'none',
                      borderRadius: isWasteModule ? '0' : '0 5px 5px 0'
                    }}
                  >
                    <icons.RemovePerson
                      colour="currentColor"
                      size={sizes.MEDIUM}
                    />
                    <Spacer.Fine />
                    Unassign
                  </ColourButton.Basic>
                )}

                {isWasteModule && (
                  <Tooltip
                    title={
                      selectedEventsContainNoWasteCarrierLicence ? (
                        ''
                      ) : (
                        <span>
                          Bulk updates are only available for
                          <br />
                          'No waste carrier licence' alerts
                        </span>
                      )
                    }
                    placement="top"
                  >
                    <ColourButton.Basic
                      type="button"
                      data-testid="Update"
                      onClick={handleUpdateBtnClick}
                      disabled={
                        working || !selectedEventsContainNoWasteCarrierLicence
                      }
                      className={showUpdateSection ? 'selected' : ''}
                      style={{
                        borderLeft: 'none',
                        borderRadius: '0 5px 5px 0'
                      }}
                    >
                      <AntIcons.SyncOutlined />
                      <Spacer.Fine />
                      Update
                    </ColourButton.Basic>
                  </Tooltip>
                )}
              </AssignmentButtonGroup>
            )}

            <Spacer.Small />

            <ColourButton.Caution
              type="button"
              onClick={handleDismissBtnClick}
              disabled={working}
            >
              <icons.ClearAll />
              <Spacer.Fine />
              Dismiss
            </ColourButton.Caution>

            <Clickable
              size="small"
              title="Close"
              onClick={() => onClose(false)}
              disabled={working}
            >
              <icons.Cross size={sizes.X_SMALL} colour="inherit" />
            </Clickable>
          </ActionStripCard>

          {isProjectAdmin && showAssignmentStrip && (
            <AssignmentStrip
              users={projectUsers.users}
              onApply={handleRecordsAssign}
              eventCount={validSelectedEventKeys.length}
              recordCount={relatedRecords.size}
              maxAssignment={MAX_ASSIGNMENT_LIMIT}
            />
          )}

          {isProjectAdmin && showUnassignmentStrip && (
            <UnassignmentStrip
              users={
                selectedEventsContainUnloaded
                  ? projectUsers.users
                  : relatedAssignees
              }
              onApply={handleRecordsUnassign}
              eventCount={validSelectedEventKeys.length}
              recordCount={relatedRecords.size}
              maxAssignment={MAX_ASSIGNMENT_LIMIT}
            />
          )}

          {isProjectAdmin && showUpdateSection && (
            <UpdateSection
              onApply={() => {
                setShowUpdateSection(false)
                setTimeout(() => {
                  onClose?.(true)
                }, 500)
              }}
              eventCount={validSelectedEventKeys.length}
              recordIds={Array.from(relatedRecords)}
              recordCount={relatedRecords.size}
              selectedEventKeys={validSelectedEventKeys}
              eventRecordMapping={eventRecordMapping}
              maxUpdate={MAX_UPDATE}
            />
          )}

          {showLongRunningOperationModal && (
            <AssignmentLongRunningOperationModal />
          )}

          <Modal
            title="Dismissing alerts"
            open={visible && showDismissConfirmationModal}
            onOk={handleEventsDismissal}
            onCancel={handleDismissModalCancel}
            transitionName=""
            footer={[
              <ColourButton.Basic
                key="back"
                type="button"
                onClick={handleDismissModalCancel}
                style={{ display: 'inline-block', marginRight: '.75rem' }}
              >
                Cancel
              </ColourButton.Basic>,
              <ColourButton.Action
                key="submit"
                type="button"
                onClick={handleEventsDismissal}
                style={{ display: 'inline-block' }}
              >
                OK
              </ColourButton.Action>
            ]}
          >
            {validSelectedEventKeys.length > MAX_DISMISSALS && (
              <>
                <p>{`You have selected ${validSelectedEventKeys.length + projectLevelAlertIds.length} alerts. A maximum of ${MAX_DISMISSALS} can be dismissed at a time.`}</p>
                <p>
                  {`Would you like to dismiss the first ${MAX_DISMISSALS} alerts of your selection? This
                operation cannot be undone.`}
                </p>
              </>
            )}
            {validSelectedEventKeys.length <= MAX_DISMISSALS && (
              <p>{`Dismiss the selected ${validSelectedEventKeys.length + projectLevelAlertIds.length > 1 ? `${validSelectedEventKeys.length + projectLevelAlertIds.length} ` : ''}alert${validSelectedEventKeys.length + projectLevelAlertIds.length > 1 ? 's' : ''}? This operation cannot be undone.`}</p>
            )}
          </Modal>
        </InnerContainer>
      </Draggable>
    </Container>
  )
}

ActionStrip.propTypes = {
  eventKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func
}
